import { memo, useCallback } from "react";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronUp } from "lucide-react";
import { TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { SortingState, SortDirection } from "@tanstack/react-table";

// ============================================================================
// STABLE TABLE HEADER
// 
// This component renders table headers INDEPENDENTLY of TanStack Table's
// internal object recreation. Headers only re-render when:
// - Column definitions change (they don't - they're memoized)
// - Sorting state changes (only on user click)
// 
// This is how professional trading platforms (Binance, HyperLiquid) solve
// the header flickering problem.
// ============================================================================

interface StableHeaderCellProps {
  columnId: string;
  headerContent: React.ReactNode;
  canSort: boolean;
  sortDirection: SortDirection | false;
  onSort: (columnId: string) => void;
  align?: "left" | "right";
}

/**
 * Stable header cell that doesn't depend on TanStack Table object instances.
 * Only re-renders when sorting state for THIS column changes.
 */
const StableHeaderCell = memo(function StableHeaderCell({
  columnId,
  headerContent,
  canSort,
  sortDirection,
  onSort,
  align = "left",
}: StableHeaderCellProps) {
  const handleClick = useCallback(() => {
    if (canSort) {
      onSort(columnId);
    }
  }, [canSort, columnId, onSort]);

  return (
    <TableHead>
      <div
        className={cn(
          "flex items-center",
          align === "right" && "justify-end",
          align === "left" && "justify-start"
        )}
      >
        <button
          type="button"
          className={cn(
            "flex gap-1 items-center py-1.5 rounded-sm font-normal",
            canSort && "cursor-pointer select-none",
            align === "right" && "flex-row-reverse",
            !sortDirection && align === "right" && "ml-4",
            !sortDirection && align === "left" && "mr-4"
          )}
          onClick={handleClick}
          disabled={!canSort}
        >
          {headerContent}
          {sortDirection === "asc" && <ChevronDown className="w-3 h-3" />}
          {sortDirection === "desc" && <ChevronUp className="w-3 h-3" />}
        </button>
      </div>
    </TableHead>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if these specific things change
  return (
    prevProps.columnId === nextProps.columnId &&
    prevProps.sortDirection === nextProps.sortDirection &&
    prevProps.canSort === nextProps.canSort &&
    prevProps.align === nextProps.align
    // Note: headerContent and onSort are stable due to useMemo/useCallback
  );
});

// Column metadata extracted from column definitions
interface ColumnMeta {
  id: string;
  headerLabel: string;
  align: "left" | "right";
  canSort: boolean;
}

interface StableTableHeaderProps {
  sorting: SortingState;
  onSort: (columnId: string) => void;
  columnMeta: ColumnMeta[];
}

/**
 * Stable table header component that renders headers independently.
 * 
 * CRITICAL: This component does NOT use TanStack Table's getHeaderGroups().
 * Instead, it renders headers directly from the column metadata, ensuring
 * headers NEVER re-render due to data changes.
 */
export const StableTableHeader = memo(function StableTableHeader({
  columnMeta,
  sorting,
  onSort,
}: StableTableHeaderProps) {
  // Create a map of column ID -> sort direction for quick lookup
  const sortMap = new Map<string, SortDirection>();
  for (const sort of sorting) {
    sortMap.set(sort.id, sort.desc ? "desc" : "asc");
  }

  return (
    <TableHeader className="sticky top-0 bg-table-row z-1">
      <TableRow className="hover:bg-transparent transition-none text-xs">
        {columnMeta.map((col) => (
          <StableHeaderCell
            key={col.id}
            columnId={col.id}
            headerContent={col.headerLabel}
            canSort={col.canSort}
            sortDirection={sortMap.get(col.id) || false}
            onSort={onSort}
            align={col.align}
          />
        ))}
      </TableRow>
    </TableHeader>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if sorting actually changes
  if (prevProps.sorting.length !== nextProps.sorting.length) return false;
  for (let i = 0; i < prevProps.sorting.length; i++) {
    if (prevProps.sorting[i].id !== nextProps.sorting[i].id) return false;
    if (prevProps.sorting[i].desc !== nextProps.sorting[i].desc) return false;
  }
  // columnMeta is stable (comes from useMemo), onSort is stable (useCallback)
  return true;
});

export type { ColumnMeta };
